import { useState, useEffect } from "react";
import { Segment, Menu, MenuItem, Icon } from "semantic-ui-react";
import { Post } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { PostItem } from "../PostItem";
import { socket } from "../../../../utils";
import "./PostList.scss";

const postController = new Post();

export function PostList(props) {
  const { filters, reload, onReload, currentPage, setCurrentPage } = props;
  const { accessToken } = useAuth();
  const [postList, setPostList] = useState({ data: [], pagination: {} });
  const [posts, setPosts] = useState(postList.data || []);

  useEffect(() => {
    (async () => {
      try {
        setPostList({ data: [], pagination: {} });
        const response = await postController.getPosts(
          accessToken,
          filters,
          currentPage
        );
        setPostList(response);
      } catch (error) {
        console.error(error);
      }
    })();
  }, [filters, reload, currentPage, accessToken]);

  useEffect(() => {
    setPosts(postList.data || []);
  }, [postList]);

  useEffect(() => {
    const handlePostCreated = (post) => {
      if (currentPage !== 1) return;
      const effectiveStatus = filters?.status ? parseInt(filters.status, 10) : 1;
      if (post.status !== effectiveStatus) return;
      if (filters?.subjectId && String(post.subjectId) !== String(filters.subjectId)) return;
      setPostList((prev) => ({
        ...prev,
        data: [post, ...(prev.data || [])],
        pagination: {
          ...prev.pagination,
          totalItems: (prev.pagination?.totalItems || 0) + 1,
        },
      }));
    };
    socket.on("postCreated", handlePostCreated);
    return () => {
      socket.off("postCreated", handlePostCreated);
    };
  }, [currentPage, filters]);

  const getTotalPages = () => {
    if (!postList?.pagination) return 1;
    return postList.pagination.totalPages || 1;
  };

  const getPaginationInfo = () => {
    if (!postList?.pagination || !posts) return "No hay datos disponibles";

    const itemsPerPage = postList.pagination.limit || 10;
    const totalItems =
      postList.pagination.totalItems || postList.pagination.total || 0;
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    return `Mostrando ${startItem}-${endItem} de ${totalItems} registros`;
  };

  const renderPageNumbers = () => {
    const totalPages = getTotalPages();
    const pages = [];
    const maxVisiblePages = 5;
    let startPage, endPage;

    if (totalPages <= maxVisiblePages) {
      startPage = 1;
      endPage = totalPages;
    } else {
      const maxPagesBeforeCurrentPage = Math.floor(maxVisiblePages / 2);
      const maxPagesAfterCurrentPage = Math.ceil(maxVisiblePages / 2) - 1;

      if (currentPage <= maxPagesBeforeCurrentPage) {
        startPage = 1;
        endPage = maxVisiblePages;
      } else if (currentPage + maxPagesAfterCurrentPage >= totalPages) {
        startPage = totalPages - maxVisiblePages + 1;
        endPage = totalPages;
      } else {
        startPage = currentPage - maxPagesBeforeCurrentPage;
        endPage = currentPage + maxPagesAfterCurrentPage;
      }
    }

    // Agregar primera página si no está visible
    if (startPage > 1) {
      pages.push(
        <MenuItem as="a" key={1} onClick={() => setCurrentPage(1)}>
          1
        </MenuItem>
      );

      if (startPage > 2) {
        pages.push(
          <MenuItem as="a" key="ellipsis-start" className="ellipsis" disabled>
            ...
          </MenuItem>
        );
      }
    }

    // Páginas visibles
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <MenuItem
          as="a"
          key={i}
          active={i === currentPage}
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </MenuItem>
      );
    }

    // Agregar última página si no está visible
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(
          <MenuItem as="a" key="ellipsis-end" className="ellipsis" disabled>
            ...
          </MenuItem>
        );
      }

      pages.push(
        <MenuItem
          as="a"
          key={totalPages}
          onClick={() => setCurrentPage(totalPages)}
        >
          {totalPages}
        </MenuItem>
      );
    }

    return pages;
  };

  return (
    <>
      <Segment className="admin-forum-post-list">
        {posts.length === 0 ? (
          <div className="admin-forum-post-list__empty">
            <Icon name="file alternate outline" size="huge" />
            <p>No existen posts con los filtros seleccionados</p>
          </div>
        ) : (
          posts.map((post) => (
            <PostItem key={post.id} post={post} onReload={onReload} />
          ))
        )}
      </Segment>

      {postList?.pagination && posts.length > 0 && (
        <div className="post-list__pagination">
          <div className="post-list__pagination-info">
            <span>{getPaginationInfo()}</span>
          </div>

          <Menu floated="right" pagination>
            <MenuItem
              as="a"
              icon
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(1)}
              title="Primera página"
            >
              <Icon name="angle double left" />
            </MenuItem>
            <MenuItem
              as="a"
              icon
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
              title="Página anterior"
            >
              <Icon name="angle left" />
            </MenuItem>
            {renderPageNumbers()}
            <MenuItem
              as="a"
              icon
              disabled={currentPage === getTotalPages()}
              onClick={() => setCurrentPage(currentPage + 1)}
              title="Página siguiente"
            >
              <Icon name="angle right" />
            </MenuItem>
            <MenuItem
              as="a"
              icon
              disabled={currentPage === getTotalPages()}
              onClick={() => setCurrentPage(getTotalPages())}
              title="Última página"
            >
              <Icon name="angle double right" />
            </MenuItem>
          </Menu>
        </div>
      )}
    </>
  );
}
